/**
 * @author NikolaV
 */
import { Ajax2 } from '@shared/Ajax';


function fetchUsers(organizationUuid: string, projectUuid: string, limit: number, offset: number, username?: string, email?: string): Promise<any> {
    const data: any = {
        limit,
        page: offset / limit,
        ...(username ? { username: '%' + username + '%' } : null),
        ...(email ? { email: '%' + email + '%' } : null)
    };

    if (organizationUuid) data.organizationUuid = organizationUuid;
    if (projectUuid) data.projectUuid = projectUuid;

    return Ajax2({
        url: kuura.getIamAddress() + '/users',
        useToken: true,
        data
    });
}

function fetchUserByUuid(uuid: string): Promise<IUserIam> {
    return Ajax2({
        url: kuura.getIamAddress() + '/user/' + uuid,
        useToken: true
    });
}

function createUser(user: IUserIam): Promise<{ uuid: string }> {
    return Ajax2({
        url: kuura.getIamAddress() + '/user',
        useToken: true,
        method: 'post',
        data: user,
        contentType: 'json'
    })
}

function updateUser(user: IUserIam): Promise<void> {
    return Ajax2({
        url: kuura.getIamAddress() + '/user',
        useToken: true,
        method: 'put',
        data: user,
        contentType: 'json'
    })
}

function setUserOrganization(userUuid: string, organizationUuid: string | null): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/organization/${organizationUuid}`,
        method: 'put',
        contentType: 'urlencoded'
    });
}

function fetchInvitationsForUser(inviteeUuid: string): Promise<IInvitation[]> {
    return Ajax2({
        url: kuura.getIamAddress() + `/invitations`,
        method: 'get',
        useToken: true,
        data: {
            inviteeUuid
        }
    });
}

function sendInvitation(inviteeUuid: string, groups: string[], roles: string[], message: string = null): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/invitation`,
        method: 'post',
        useToken: true,
        contentType: 'json',
        data: {
            sender: {
                uuid: kuura.getUserUuid()
            },
            invitee: {
                uuid: inviteeUuid
            },
            groups: (groups || []).map(groupUuid => ({ uuid: groupUuid })),
            roles: (roles || []).map(roleUuid => ({ uuid: roleUuid })),
            link: window.location.origin,
            message
        }
    });
}

function cancelInvitation(invitationUuid: string): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/invitation/${invitationUuid}/cancel`,
        method: 'patch',
        useToken: true
    });
}

function acceptInvitation(invitationUuid: string): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/invitation/${invitationUuid}`,
        method: 'put',
        useToken: true,
        data: {
            status: 'accepted'
        }
    });
}

function rejectInvitation(invitationUuid: string): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/invitation/${invitationUuid}`,
        method: 'put',
        useToken: true,
        data: {
            status: 'rejected'
        }
    });
}

function fetchGroupsForUser(userUuid: string): Promise<IUserGroup[]> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/groups`,
        method: 'get',
        useToken: true,
        contentType: 'urlencoded'
    });
}

function addGroupToUser(userUuid: string, groupUuid: string): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/group/${groupUuid}`,
        method: 'put',
        useToken: true,
        contentType: 'urlencoded'
    });
}

function removeGroupFromUser(userUuid: string, groupUuid: string): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/group/${groupUuid}`,
        method: 'delete',
        useToken: true,
        contentType: 'urlencoded'
    });
}

function fetchRolesForUser(userUuid: string): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/roles`,
        method: 'get',
        useToken: true,
        contentType: 'urlencoded'
    });
}

function addRoleToUser(userUuid: string, roleUuid: string): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/role/${roleUuid}`,
        method: 'put',
        useToken: true,
        contentType: 'urlencoded'
    });
}

function removeRoleFromUser(userUuid: string, roleUuid: string): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/role/${roleUuid}`,
        method: 'delete',
        useToken: true,
        contentType: 'urlencoded'
    });
}

function getUserSyncStatus(userUuid: string): Promise<any> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/sync-status`,
        method: 'get',
        useToken: true,
        contentType: 'urlencoded'
    });
}

export type IUserSettingNotifyValue = 'default_reject' | 'default_accept' | 'informed_reject' | 'informed_accept' | '';

export interface IUserSetting {
    key: string,
    value: IUserSettingNotifyValue,
    updated: string,
    isChanged?: boolean // used by ui to track if it needs saving
}

function getUserSettings(userUuid: string, prefix?: string): Promise<IUserSetting[]> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/settings?prefix=${prefix}`,
        method: 'get',
        useToken: true,
        contentType: 'urlencoded'
    });
}

function updateUserSettings(userUuid: string, data: IUserSetting[]): Promise<void> {
    return Ajax2({
        url: kuura.getIamAddress() + `/user/${userUuid}/settings`,
        method: 'post',
        useToken: true,
        contentType: 'json',
        data
    });
}

export const UsersIamService = {
    fetchUsers,
    fetchUserByUuid,
    createUser,
    updateUser,
    setUserOrganization,
    getUserSyncStatus,
    settings: {
        list: getUserSettings,
        update: updateUserSettings
    },
    invitations: {
        fetchForUser: fetchInvitationsForUser,
        accept: acceptInvitation,
        reject: rejectInvitation,
        sendInvitation,
        cancelInvitation
    },
    groups: {
        fetchForUser: fetchGroupsForUser,
        add: addGroupToUser,
        remove: removeGroupFromUser
    },
    roles: {
        fetchForUser: fetchRolesForUser,
        add: addRoleToUser,
        remove: removeRoleFromUser
    }
};

